import { cn } from '@/lib/utils';

/**
 * The sign-in panel's illustration: learning that compounds into a career.
 *
 * Drawn inline rather than shipped as an asset, for the same reason the fonts are
 * bundled — this is the first page every user sees, and an illustration that arrives
 * on its own request paints after the card has already landed. Inline it also reads
 * the palette: every fill and stroke resolves through `--primary` and `--secondary`,
 * so a tenant that white-labels its action colour re-tints the artwork with it
 * instead of being handed a green PNG.
 *
 * The vocabulary is geometric on purpose — cards, a rising series, a cohort of
 * avatars, a mortarboard, a credential. A government or defence procurement panel is
 * the audience for this screen, and figurative illustration reads as consumer
 * software to them.
 *
 * ---- Composition -----------------------------------------------------------
 *
 * Four objects, and the overlaps are the layout. Each one is drawn after whatever it
 * is meant to sit in front of, and every overlap lands on another object's *padding*
 * rather than on its content — the chart's baseline is at y=296 and nothing crosses
 * it, so the series is never clipped by the cards stacked over the card it sits in.
 * Change a rectangle here and check that invariant before anything else.
 *
 * Purely decorative: the panel's meaning is carried by the caption beside it, so the
 * whole thing is hidden from assistive technology.
 */
export function AuthIllustration({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 520 480"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="presentation"
      aria-hidden="true"
      className={cn('h-auto w-full', className)}
    >
      {/* ---- Ambient field ------------------------------------------------ */}
      {/* Indigo at 5–7%: the depth behind the composition, never an edge. */}
      <ellipse cx="140" cy="112" rx="148" ry="116" className="fill-primary/[0.06]" />
      <ellipse cx="404" cy="386" rx="150" ry="120" className="fill-primary/[0.05]" />

      <circle cx="256" cy="234" r="196" className="stroke-primary/[0.09]" strokeWidth="1" />
      <circle
        cx="256"
        cy="234"
        r="152"
        className="stroke-primary/[0.10]"
        strokeWidth="1"
        strokeDasharray="3 9"
      />

      {/* The floor. One soft ellipse is enough to seat the group; a cast shadow per
          card would read as clip art. */}
      <ellipse cx="256" cy="436" rx="172" ry="15" className="fill-primary/[0.05]" />

      {/* ---- The series -- career growth ---------------------------------- */}
      {/* Drawn first and largest: it is the object the eye should land on, and
          everything else overlaps a corner of it. */}
      <g>
        <rect
          x="110"
          y="120"
          width="290"
          height="210"
          rx="22"
          className="fill-card stroke-primary/10"
          strokeWidth="1.5"
        />

        {/* Card header */}
        <rect x="136" y="150" width="88" height="9" rx="4.5" className="fill-primary/20" />
        <rect x="136" y="168" width="56" height="7" rx="3.5" className="fill-primary/10" />
        <rect x="286" y="148" width="52" height="22" rx="11" className="fill-secondary/60" />
        <path
          d="M300 162l6-7 6 7"
          className="stroke-primary"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Gridlines, then the series over them. Dashed and at 8% so they measure the
            chart without being read as data. */}
        <path
          d="M136 228h238M136 262h238"
          className="stroke-primary/[0.08]"
          strokeWidth="1"
          strokeDasharray="2 8"
        />
        <path d="M136 296h238" className="stroke-primary/[0.14]" strokeWidth="1" />

        {/* Area under the series — the same indigo at 8%, which is what makes the line
            read as a quantity rather than as a route on a map. */}
        <path
          d="M136 296v-34l60 10 60-40 60 12 58-52v104z"
          className="fill-primary/[0.08]"
        />
        <path
          d="M136 262l60 10 60-40 60 12 58-52"
          className="stroke-primary"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Nodes. Card-filled so the line passes behind them cleanly; the terminal
            node is filled instead, because it is the one the eye should land on. */}
        <circle cx="196" cy="272" r="4" className="fill-card stroke-primary" strokeWidth="2" />
        <circle cx="256" cy="232" r="4" className="fill-card stroke-primary" strokeWidth="2" />
        <circle cx="316" cy="244" r="4" className="fill-card stroke-primary" strokeWidth="2" />
        <circle cx="374" cy="192" r="9" className="fill-primary/15" />
        <circle cx="374" cy="192" r="5" className="fill-primary" />
      </g>

      {/* ---- Cohort card, over the bottom-left corner --------------------- */}
      <g>
        <rect
          x="56"
          y="300"
          width="212"
          height="94"
          rx="20"
          className="fill-card stroke-primary/10"
          strokeWidth="1.5"
        />

        {/* Overlapping avatars: a cohort, at the only scale where abstract circles
            still read as people. Each carries a card-coloured ring so the stack
            separates instead of merging into a bar. */}
        <circle cx="88" cy="334" r="15" className="fill-secondary stroke-card" strokeWidth="3" />
        <circle
          cx="112"
          cy="334"
          r="15"
          className="fill-primary/25 stroke-card"
          strokeWidth="3"
        />
        <circle cx="136" cy="334" r="15" className="fill-primary stroke-card" strokeWidth="3" />
        <circle
          cx="160"
          cy="334"
          r="15"
          className="fill-card stroke-primary/25"
          strokeWidth="1.5"
          strokeDasharray="3 4"
        />
        <path
          d="M160 328v12M154 334h12"
          className="stroke-primary/50"
          strokeWidth="1.6"
          strokeLinecap="round"
        />

        <rect x="88" y="364" width="148" height="8" rx="4" className="fill-primary/10" />
        <rect x="88" y="364" width="96" height="8" rx="4" className="fill-secondary" />
      </g>

      {/* ---- Credential chip, over the bottom-right corner ---------------- */}
      <g>
        <rect
          x="316"
          y="318"
          width="154"
          height="72"
          rx="20"
          className="fill-card stroke-primary/10"
          strokeWidth="1.5"
        />
        {/* Rosette: disc, ring, two ribbon tails. */}
        <circle cx="350" cy="350" r="17" className="fill-secondary" />
        <circle cx="350" cy="350" r="8" className="fill-primary" />
        <path
          d="M342 365l-4 16 12-6 12 6-4-16"
          className="stroke-primary/40"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect x="382" y="340" width="62" height="8" rx="4" className="fill-primary/20" />
        <rect x="382" y="356" width="40" height="7" rx="3.5" className="fill-primary/10" />
      </g>

      {/* ---- Credential badge, over the top-right corner ------------------ */}
      {/* A disc rather than a fourth card: at this point in the stack another
          rectangle would read as clutter, and a circle overlaps one cleanly. */}
      <g>
        <circle cx="420" cy="130" r="52" className="fill-secondary" />
        <circle cx="420" cy="130" r="52" className="stroke-card" strokeWidth="3" />
        {/* Mortarboard: board, body, tassel. */}
        <path d="M420 106l24 11-24 11-24-11z" className="fill-primary" />
        <path
          d="M406 124v12c0 4.4 6.3 8 14 8s14-3.6 14-8v-12"
          className="stroke-primary"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          d="M444 117v16"
          className="stroke-primary"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <circle cx="444" cy="137" r="3.5" className="fill-primary" />
      </g>

      {/* ---- Punctuation --------------------------------------------------- */}
      <circle cx="74" cy="136" r="5" className="fill-primary/20" />
      <circle cx="486" cy="248" r="6" className="fill-secondary" />
      <circle cx="272" cy="74" r="4" className="fill-primary/15" />
      <circle cx="46" cy="256" r="3.5" className="fill-primary/20" />
    </svg>
  );
}
